import { SAMPLE_STATUS } from '../constants/sample.constants';
import type { SampleListStatId } from '../components/samples/SampleListStatsCards';
import type { SampleListParams } from '../services/sample.service';
import {
  computeDateRange,
  normalizeDateToYYYYMMDD,
  type DateRangeDirection,
  type DateRangeUnit,
} from './sampleDateRange';
import { sampleTypeCodeToLegacyJa } from './sampleTypeLabels';

const STAT_ID_TO_STATUS: Record<Exclude<SampleListStatId, 'total'>, string> = {
  available: SAMPLE_STATUS.AVAILABLE,
  outOfStock: SAMPLE_STATUS.OUT_OF_STOCK,
  disposed: SAMPLE_STATUS.DISPOSED,
};

/** フィルター status → 統計ストリップの activeStat */
export function statusToStatId(status: string | undefined): SampleListStatId {
  if (status === SAMPLE_STATUS.AVAILABLE) return 'available';
  if (status === SAMPLE_STATUS.OUT_OF_STOCK) return 'outOfStock';
  if (status === SAMPLE_STATUS.DISPOSED) return 'disposed';
  return 'total';
}

/**
 * 統計ストリップ選択を filters に反映。
 * 同じカードの再クリック、または「総数」は status を外して全件に戻す。
 */
export function applyStatIdToFilters(
  prev: SampleListParams,
  id: SampleListStatId
): SampleListParams {
  if (id === 'total' || statusToStatId(prev.status) === id) {
    const next = { ...prev };
    delete next.status;
    return next;
  }
  return { ...prev, status: STAT_ID_TO_STATUS[id] };
}

/** `?stat=` クエリ（概要カード等からのディープリンク）。トグルせず強制適用する。 */
export function parseSampleListStatQuery(raw: string | null): SampleListStatId | null {
  if (raw === 'total' || raw === 'available' || raw === 'outOfStock' || raw === 'disposed') {
    return raw;
  }
  return null;
}

export function applyStatQueryToFilters(
  prev: SampleListParams,
  id: SampleListStatId
): SampleListParams {
  if (id === 'total') {
    const next = { ...prev };
    delete next.status;
    return next;
  }
  return { ...prev, status: STAT_ID_TO_STATUS[id] };
}

/** 一覧の初期表示: 残数量>0 のみ（残なしはストリップで示唆） */
export const DEFAULT_SAMPLE_LIST_STATUS_FILTERS: Pick<SampleListParams, 'status'> = {
  status: SAMPLE_STATUS.AVAILABLE,
};

/**
 * ユーザーが意図した詳細条件があるか。
 * 既定の「利用可能」だけの status はフィルター扱いしない（クリア導線・ラベル用）。
 */
export function hasNonDefaultSampleListFilters(filters: SampleListParams): boolean {
  const api = buildSampleListApiParams(filters);
  const keys = Object.keys(api).filter((key) => {
    const value = api[key as keyof SampleListParams];
    return value !== undefined && value !== null && value !== '';
  });
  if (keys.length === 0) return false;
  if (
    keys.length === 1 &&
    keys[0] === 'status' &&
    api.status === SAMPLE_STATUS.AVAILABLE
  ) {
    return false;
  }
  return true;
}

function applyDateRangeToParams(
  out: SampleListParams,
  center: string | undefined,
  dir: DateRangeDirection,
  val: number,
  unit: DateRangeUnit,
  startKey: 'createdDateStart' | 'lastUsedDateStart',
  endKey: 'createdDateEnd' | 'lastUsedDateEnd'
) {
  if (!center) return;
  const range = computeDateRange(normalizeDateToYYYYMMDD(center), dir, val, unit);
  if (range) {
    out[startKey] = range.start;
    out[endKey] = range.end;
  }
}

/** UI フィルター状態を sample.list API 用パラメータに変換 */
export function buildSampleListApiParams(filters: SampleListParams): SampleListParams {
  const {
    createdDate,
    createdDateDir,
    createdDateVal,
    createdDateUnit,
    lastUsedDate,
    lastUsedDateDir,
    lastUsedDateVal,
    lastUsedDateUnit,
    ...rest
  } = filters;
  const out: SampleListParams = { ...rest };
  if (out.sampleType) {
    const legacy = sampleTypeCodeToLegacyJa(out.sampleType);
    if (legacy !== undefined) out.sampleType = legacy;
  }
  applyDateRangeToParams(
    out,
    createdDate,
    createdDateDir ?? 'both',
    createdDateVal ?? 0,
    createdDateUnit ?? 'day',
    'createdDateStart',
    'createdDateEnd'
  );
  applyDateRangeToParams(
    out,
    lastUsedDate,
    lastUsedDateDir ?? 'both',
    lastUsedDateVal ?? 0,
    lastUsedDateUnit ?? 'day',
    'lastUsedDateStart',
    'lastUsedDateEnd'
  );
  return out;
}
